/**
 * @file App.tsx
 * @description Main application routing and domain gateway for TableGreet Venue Admin.
 * 
 * ARCHITECTURE OVERVIEW:
 * 1. Domain Detection (DomainRouter):
 *    - app.tablegreet.com / localhost:5173 -> Admin HQ dashboard & staff consoles.
 *    - venue.tablegreet.com -> Platform public digital menu browsing (hosted on Vercel).
 *    - Custom domains (e.g. restaurant.com) -> Custom-branded guest menu or digital TV signage.
 * 2. Guard System:
 *    - RootAuthGateway: Smart landing redirector based on authentication status and user role.
 *    - ProtectedRoute: Authenticates user session, email verification, and venue association.
 *    - RoleGuard: Verifies granular permissions (OWNER, MANAGER, CHEF, WAITER, ADMIN).
 * 3. Telemetry & Error Tracking:
 *    - Wrapped in Sentry route instrumentation and PostHog page tracking.
 */

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { usePostHog } from '@posthog/react';
import { ChefHat, UtensilsCrossed } from 'lucide-react';

import { ScrollToTop } from './components/ScrollToTop';
import { VenueProvider } from './context/VenueContext';
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';
import { AdminLayout } from './components/AdminLayout';
import { ProtectedRoute, RoleGuard, RootAuthGateway } from './components/auth/Guards';

/**
 * Recovers from stale Vite chunk hashes after a new production deployment.
 * If a dynamic import fails due to outdated cache/CDN chunks, this will trigger
 * a single controlled hard reload to fetch the latest assets from Cloudflare Pages.
 * 
 * DEBUGGING TIP: If users report blank pages after an update, inspect `tg_chunk_reload_ts`
 * in sessionStorage to verify if this reload safeguard fired.
 */
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error: any) {
      const isChunkError =
        error?.message?.includes('dynamically imported module') ||
        error?.message?.includes('Importing a module script failed') ||
        error?.message?.includes('is not a valid JavaScript MIME type') ||
        error?.message?.includes('Expected a JavaScript-or-Wasm module script') ||
        error?.name === 'ChunkLoadError';

      if (isChunkError) {
        const lastReload = sessionStorage.getItem('tg_chunk_reload_ts');
        const now = Date.now();
        // Prevent continuous reload loops if there is an actual network outage (10s threshold)
        if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
          sessionStorage.setItem('tg_chunk_reload_ts', String(now));
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw error;
    }
  });
}

// Auth Pages
const LoginView = lazyRetry(() => import('./pages/LoginView').then(m => ({ default: m.LoginView })));
const RegisterView = lazyRetry(() => import('./pages/RegisterView').then(m => ({ default: m.RegisterView })));
const AuthCallbackView = lazyRetry(() => import('./pages/AuthCallbackView').then(m => ({ default: m.AuthCallbackView })));
const AcceptInviteView = lazyRetry(() => import('./pages/AcceptInviteView').then(m => ({ default: m.AcceptInviteView })));
const ForgotPasswordView = lazyRetry(() => import('./pages/ForgotPasswordView').then(m => ({ default: m.ForgotPasswordView })));
const ResetPasswordView = lazyRetry(() => import('./pages/ResetPasswordView').then(m => ({ default: m.ResetPasswordView })));

// Checkout
const CheckoutView = lazyRetry(() => import('./pages/CheckoutView'));

// Staff Interfaces - Interstitial for dedicated independent consoles
const StaffConsoleRedirect: React.FC<{ target: 'chef' | 'waiter' }> = ({ target }) => {
  const isLocal = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
  const dest = target === 'chef'
    ? (isLocal ? 'http://localhost:2004' : 'https://chef.tablegreet.com')
    : (isLocal ? 'http://localhost:2005' : 'https://waiter.tablegreet.com');

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-6 text-center">
      <div className="max-w-md w-full bg-zinc-900 border border-white/10 rounded-2xl p-8 space-y-6 shadow-2xl">
        <div className="w-16 h-16 rounded-full bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center mx-auto text-brand-primary">
          {target === 'chef' ? <ChefHat size={32} /> : <UtensilsCrossed size={32} />}
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-white capitalize">{target} Workstation</h2>
          <p className="text-sm text-zinc-400">
            Specialized staff workstations are hosted on their own dedicated independent consoles.
          </p>
        </div>
        <div className="pt-2 flex flex-col gap-3">
          <a
            href={dest}
            className="w-full py-3.5 rounded-xl bg-brand-primary text-white font-bold hover:bg-brand-primary/90 transition-all text-sm block"
          >
            Launch {target === 'chef' ? 'Chef Kitchen Display' : 'Waiter Console'}
          </a>
          <Link
            to="/admin"
            className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-zinc-400 hover:text-white font-bold transition-all text-sm block"
          >
            Return to Venue Admin
          </Link>
        </div>
      </div>
    </div>
  );
};

// Digital TV Signage - Redirect to dedicated standalone engine
const SignageConsoleRedirect: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:2007' : 'https://signage.tablegreet.com';
    window.location.replace(`${base}${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center text-zinc-400">
      <div className="w-8 h-8 border-2 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin mb-3" />
      <p className="text-sm font-semibold">Redirecting to TableGreet Digital Signage...</p>
    </div>
  );
};

// Admin HQ
const AnalyticsView = lazyRetry(() => import('./pages/admin/AnalyticsView').then(m => ({ default: m.AnalyticsView })));
const VenueInsights = lazyRetry(() => import('./pages/admin/VenueInsights').then(m => ({ default: m.VenueInsights })));
const ItemManager = lazyRetry(() => import('./pages/ItemManager').then(m => ({ default: m.ItemManager })));
const CategoryManager = lazyRetry(() => import('./pages/CategoryManager').then(m => ({ default: m.CategoryManager })));
const SettingsView = lazyRetry(() => import('./pages/SettingsView').then(m => ({ default: m.SettingsView })));
const BillingView = lazyRetry(() => import('./pages/admin/BillingView').then(m => ({ default: m.BillingView })));
const QRGenerator = lazyRetry(() => import('./pages/QRGenerator').then(m => ({ default: m.QRGenerator })));
const DepartmentManager = lazyRetry(() => import('./pages/DepartmentManager').then(m => ({ default: m.DepartmentManager })));
const IngredientManager = lazyRetry(() => import('./pages/IngredientManager').then(m => ({ default: m.IngredientManager })));
const ServiceHoursManager = lazyRetry(() => import('./pages/admin/ServiceHoursManager').then(m => ({ default: m.ServiceHoursManager })));
// Loyalty Suite
const LoyaltyLayout = lazyRetry(() => import('./features/loyalty/components/layout/LoyaltyLayout').then(m => ({ default: m.LoyaltyLayout })));
const LoyaltyOverviewView = lazyRetry(() => import('./features/loyalty/components/views/LoyaltyOverviewView').then(m => ({ default: m.LoyaltyOverviewView })));
const LoyaltySettingsView = lazyRetry(() => import('./features/loyalty/components/views/LoyaltySettingsView').then(m => ({ default: m.LoyaltySettingsView })));
const LoyaltyRewardsView = lazyRetry(() => import('./features/loyalty/components/views/LoyaltyRewardsView').then(m => ({ default: m.LoyaltyRewardsView })));
const LoyaltyMembersView = lazyRetry(() => import('./features/loyalty/components/views/LoyaltyMembersView').then(m => ({ default: m.LoyaltyMembersView })));
const LoyaltyTransactionsView = lazyRetry(() => import('./features/loyalty/components/views/LoyaltyTransactionsView').then(m => ({ default: m.LoyaltyTransactionsView })));
const LoyaltyCampaignsView = lazyRetry(() => import('./features/loyalty/components/views/LoyaltyCampaignsView').then(m => ({ default: m.LoyaltyCampaignsView })));
const LoyaltyAnalyticsView = lazyRetry(() => import('./features/loyalty/components/views/LoyaltyAnalyticsView').then(m => ({ default: m.LoyaltyAnalyticsView })));
const CampaignStudio = lazyRetry(() => import('./pages/admin/CampaignStudio').then(m => ({ default: m.CampaignStudio })));
const SignageManager = lazyRetry(() => import('./pages/admin/SignageManager').then(m => ({ default: m.SignageManager })));
const KitchenOps = lazyRetry(() => import('./pages/admin/KitchenOps').then(m => ({ default: m.KitchenOps })));
const ThemeEditor = lazyRetry(() => import('./pages/admin/ThemeEditor').then(m => ({ default: m.ThemeEditor })));
const MultiVenueView = lazyRetry(() => import('./pages/admin/MultiVenueView').then(m => ({ default: m.MultiVenueView })));
const AuditLogView = lazyRetry(() => import('./pages/admin/AuditLogView').then(m => ({ default: m.AuditLogView })));
const TeamView = lazyRetry(() => import('./pages/admin/TeamView').then(m => ({ default: m.TeamView })));
const ApiView = lazyRetry(() => import('./pages/admin/ApiView').then(m => ({ default: m.ApiView })));
const SsoView = lazyRetry(() => import('./pages/admin/SsoView').then(m => ({ default: m.SsoView })));
const SupportView = lazyRetry(() => import('./pages/admin/SupportView').then(m => ({ default: m.SupportView })));
const SeoGoogleView = lazyRetry(() => import('./pages/admin/SeoGoogleView').then(m => ({ default: m.SeoGoogleView })));
const NotificationsView = lazyRetry(() => import('./pages/admin/NotificationsView').then(m => ({ default: m.NotificationsView })));
const TaxComplianceView = lazyRetry(() => import('./pages/admin/TaxComplianceView').then(m => ({ default: m.TaxComplianceView })));
const NotFoundView = lazyRetry(() => import('./pages/system/NotFoundView').then(m => ({ default: m.NotFoundView })));

// Reservation Workspace (Max/Enterprise exclusive)
const ReservationGuard = lazyRetry(() => import('./features/reservations/components/workspace/ReservationGuard').then(m => ({ default: m.ReservationGuard })));
const ReservationOverview = lazyRetry(() => import('./features/reservations/components/views/ReservationOverview').then(m => ({ default: m.ReservationOverview })));
const ReservationTimeline = lazyRetry(() => import('./features/reservations/components/views/ReservationTimeline').then(m => ({ default: m.ReservationTimeline })));
const FloorPlanView = lazyRetry(() => import('./features/reservations/components/views/FloorPlanView').then(m => ({ default: m.FloorPlanView })));
const ReservationListView = lazyRetry(() => import('./features/reservations/components/views/ReservationListView').then(m => ({ default: m.ReservationListView })));
const WaitlistView = lazyRetry(() => import('./features/reservations/components/views/WaitlistView').then(m => ({ default: m.WaitlistView })));
const WalkInsView = lazyRetry(() => import('./features/reservations/components/views/WalkInsView').then(m => ({ default: m.WalkInsView })));
const GuestsView = lazyRetry(() => import('./features/reservations/components/views/GuestsView').then(m => ({ default: m.GuestsView })));
const EventsView = lazyRetry(() => import('./features/reservations/components/views/EventsView').then(m => ({ default: m.EventsView })));
const ReservationSettingsView = lazyRetry(() => import('./features/reservations/components/views/ReservationSettingsView').then(m => ({ default: m.ReservationSettingsView })));
const ReservationCalendarView = lazyRetry(() => import('./features/reservations/components/views/ReservationCalendarView').then(m => ({ default: m.ReservationCalendarView })));

// Delivery Workspace
const DeliveryOverview = lazyRetry(() => import('./features/delivery/components/DeliveryOverview').then(m => ({ default: m.DeliveryOverview })));
const DeliveryOrders = lazyRetry(() => import('./features/delivery/components/DeliveryOrders').then(m => ({ default: m.DeliveryOrders })));
const DeliveryOrderDetails = lazyRetry(() => import('./features/delivery/components/DeliveryOrderDetails').then(m => ({ default: m.DeliveryOrderDetails })));
const LiveDispatch = lazyRetry(() => import('./features/delivery/components/LiveDispatch').then(m => ({ default: m.LiveDispatch })));
const DeliveryDrivers = lazyRetry(() => import('./features/delivery/components/DeliveryDrivers').then(m => ({ default: m.DeliveryDrivers })));
const DeliveryZones = lazyRetry(() => import('./features/delivery/components/DeliveryZones').then(m => ({ default: m.DeliveryZones })));
const DeliveryCustomers = lazyRetry(() => import('./features/delivery/components/DeliveryCustomers').then(m => ({ default: m.DeliveryCustomers })));
const DeliveryPromotions = lazyRetry(() => import('./features/delivery/components/DeliveryPromotions').then(m => ({ default: m.DeliveryPromotions })));
const DeliveryAnalytics = lazyRetry(() => import('./features/delivery/components/DeliveryAnalytics').then(m => ({ default: m.DeliveryAnalytics })));
const DeliverySettings = lazyRetry(() => import('./features/delivery/components/DeliverySettings').then(m => ({ default: m.DeliverySettings })));

// Courier Mobile Interface
const CourierDashboard = lazyRetry(() => import('./pages/courier/CourierDashboard').then(m => ({ default: m.CourierDashboard })));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

/**
 * DomainRouter: Ensures routing hygiene for the TableGreet Venue Admin application.
 * Directs status page redirects and renders administrative, staff, and digital signage routes.
 */
const DomainRouter: React.FC = () => {
  const location = useLocation();
  const hostname = window.location.hostname.toLowerCase();

  // Status page check
  if (hostname === 'status.tablegreet.com') {
    window.location.href = 'https://tablegreet.com/status';
    return null;
  }

  // Safety redirect: If staff/admin tries to open dashboard routes on venue.tablegreet.com, redirect to app.
  if (hostname === 'venue.tablegreet.com') {
    const isAdminOrStaff =
      location.pathname === '/login' ||
      location.pathname === '/register' ||
      location.pathname.startsWith('/admin') ||
      location.pathname.startsWith('/chefs') ||
      location.pathname.startsWith('/waiters') ||
      location.pathname.startsWith('/courier');

    if (isAdminOrStaff) {
      window.location.href = `https://app.tablegreet.com${location.pathname}${location.search}`;
      return null;
    }
  }

  // Admin dashboard, staff consoles, and in-venue signage
  return (
    <SentryRoutes>
      {/* Root → Secure auth check and Gateway */}
      <Route path="/" element={<RootAuthGateway />} />

      {/* In-Venue Digital TV Signage - Dedicated Independent Engine */}
      <Route path="/:venueSlug/signage" element={<SignageConsoleRedirect />} />
      <Route path="/signage" element={<SignageConsoleRedirect />} />
      <Route path="/pair" element={<SignageConsoleRedirect />} />

      {/* Auth Flow */}
      <Route path="/login" element={<LoginView />} />
      <Route path="/register" element={<RegisterView />} />
      <Route path="/auth/callback" element={<AuthCallbackView />} />
      <Route path="/accept-invite" element={<AcceptInviteView />} />
      <Route path="/forgot-password" element={<ForgotPasswordView />} />
      <Route path="/reset-password" element={<ResetPasswordView />} />

      {/* Subscription & Checkout */}
      <Route path="/checkout" element={
        <ProtectedRoute>
          <CheckoutView />
        </ProtectedRoute>
      } />

      {/* Staff & Courier Interfaces */}
      <Route path="/chefs" element={<StaffConsoleRedirect target="chef" />} />
      <Route path="/waiters" element={<StaffConsoleRedirect target="waiter" />} />
      <Route path="/courier" element={
        <ProtectedRoute>
          <RoleGuard allowedRoles={['OWNER', 'MANAGER', 'ADMIN', 'COURIER', 'DRIVER']}>
            <CourierDashboard />
          </RoleGuard>
        </ProtectedRoute>
      } />

      {/* Admin HQ */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <RoleGuard allowedRoles={['OWNER', 'MANAGER', 'ADMIN']}>
            <AdminLayout />
          </RoleGuard>
        </ProtectedRoute>
      }>
        <Route index element={<AnalyticsView />} />
        <Route path="insights" element={<VenueInsights />} />
        <Route path="departments" element={<DepartmentManager />} />
        <Route path="service-hours" element={<ServiceHoursManager />} />
        <Route path="categories" element={<CategoryManager />} />
        <Route path="items" element={<ItemManager />} />
        <Route path="ingredients" element={<IngredientManager />} />
        {/* Loyalty Program Workspace inside AdminLayout */}
        <Route path="loyalty" element={<LoyaltyLayout />}>
          <Route index element={<LoyaltyOverviewView />} />
          <Route path="settings" element={<LoyaltySettingsView />} />
          <Route path="rewards" element={<LoyaltyRewardsView />} />
          <Route path="members" element={<LoyaltyMembersView />} />
          <Route path="transactions" element={<LoyaltyTransactionsView />} />
          <Route path="campaigns" element={<LoyaltyCampaignsView />} />
          <Route path="analytics" element={<LoyaltyAnalyticsView />} />
        </Route>
        <Route path="campaigns" element={<CampaignStudio />} />
        <Route path="signage" element={<SignageManager />} />
        <Route path="qr" element={<QRGenerator />} />
        <Route path="kitchen" element={<KitchenOps />} />
        <Route path="theme" element={<ThemeEditor />} />
        <Route path="governance" element={<MultiVenueView />} />
        <Route path="audit" element={<AuditLogView />} />
        <Route path="api" element={<ApiView />} />
        <Route path="sso" element={<SsoView />} />
        <Route path="team" element={<TeamView />} />
        <Route path="billing" element={<BillingView />} />
        <Route path="tax" element={<TaxComplianceView />} />
        <Route path="support" element={<SupportView />} />
        <Route path="seo" element={<SeoGoogleView />} />
        <Route path="seo/:tab" element={<SeoGoogleView />} />
        <Route path="notifications" element={<NotificationsView />} />
        <Route path="settings" element={<SettingsView />} />
        <Route path="settings/:section" element={<SettingsView />} />

        {/* Reservations Studio inside AdminLayout */}
        <Route path="reservations" element={<ReservationGuard />}>
          <Route index element={<ReservationOverview />} />
          <Route path="timeline" element={<ReservationTimeline />} />
          <Route path="calendar" element={<ReservationCalendarView />} />
          <Route path="floor-plan" element={<FloorPlanView />} />
          <Route path="list" element={<ReservationListView />} />
          <Route path="waitlist" element={<WaitlistView />} />
          <Route path="walk-ins" element={<WalkInsView />} />
          <Route path="guests" element={<GuestsView />} />
          <Route path="customers" element={<GuestsView />} />
          <Route path="events" element={<EventsView />} />
          <Route path="settings" element={<ReservationSettingsView />} />
          <Route path="settings/:tab" element={<ReservationSettingsView />} />
        </Route>

        {/* Delivery & Dispatch Workspace inside AdminLayout (dev environment only) */}
        {import.meta.env.DEV && (
          <Route path="delivery">
            <Route index element={<DeliveryOverview />} />
            <Route path="orders" element={<DeliveryOrders />} />
            <Route path="orders/:id" element={<DeliveryOrderDetails />} />
            <Route path="dispatch" element={<LiveDispatch />} />
            <Route path="drivers" element={<DeliveryDrivers />} />
            <Route path="zones" element={<DeliveryZones />} />
            <Route path="customers" element={<DeliveryCustomers />} />
            <Route path="promotions" element={<DeliveryPromotions />} />
            <Route path="analytics" element={<DeliveryAnalytics />} />
            <Route path="settings" element={<DeliverySettings />} />
          </Route>
        )}
      </Route>

      {/* Fallback */}
      <Route path="*" element={<NotFoundView />} />
    </SentryRoutes>
  );
};

const PostHogPageTracker: React.FC = () => {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    posthog?.capture('$pageview', {
      $current_url: window.location.href,
    });
  // Depend only on pathname so $pageview fires on route changes, not on every
  // query-param or hash update which would inflate pageview counts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, posthog]);

  return null;
};

const App: React.FC = () => {
  const posthog = usePostHog();

  useEffect(() => {
    posthog?.capture('app_mounted', {
      timestamp: new Date().toISOString()
    });
  }, [posthog]);

  return (
    <Router>
      <PostHogPageTracker />
      <ScrollToTop />
      <ToastProvider>
        <VenueProvider>
          <NotificationProvider>
            <Suspense fallback={
              <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', border: '4px solid rgba(255,79,0,0.2)', borderTopColor: '#ff4f00', animation: 'spin 0.8s linear infinite' }} />
              </div>
            }>
              <DomainRouter />
            </Suspense>
          </NotificationProvider>
        </VenueProvider>
      </ToastProvider>
    </Router>
  );
};

export default App;
